import React, {useEffect, useState} from 'react';
import {useForm, Controller, useFieldArray} from 'react-hook-form';
import {
  TextField,
  Typography,
  Box,
  Grid,
  Switch,
  FormControlLabel,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import MDButton from 'components/MDButton';
import {useParams, useNavigate} from 'react-router-dom';
import {Axios} from '../../services/axios';
import {Block} from 'notiflix';
import MDSnackbar from 'components/MDSnackbar';

function Dashboard() {
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: {errors},
  } = useForm({
    defaultValues: {
      name: '',
      status: false,
      settings: '',
      banners: [],
    },
  });

  const {
    fields: bannerFields,
    append,
    remove,
    update,
  } = useFieldArray({
    control,
    name: 'banners',
  });

  const navigate = useNavigate();
  const {pageId, sliderId} = useParams();

  //states
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccessSB, setShowSuccessSB] = useState(false);
  const [deletedBanners, setDeletedBanners] = useState([]);
  const [page, setPage] = useState({
    name: '',
    pageType: 'STATIC', // NEW: Add pageType
    dynamicEntities: [], // NEW: Add dynamicEntities
    route: '' // NEW: Add route
  });

  //? Hooks
  useEffect(() => {
    fetchSliderData();
  }, []);

  useEffect(() => {
    if (isLoading) Block.circle('.container', 'Loading Page Settings...');
    else Block.remove('.container');
  }, [isLoading]);

  useEffect(() => {
    if (showSuccessSB) {
      setTimeout(() => {
        closeSuccessSB();
      }, 5000);
    }
  }, [showSuccessSB]);

  //? Functions
  const closeSuccessSB = () => setShowSuccessSB(false);

  const fetchSliderData = async () => {
    setIsLoading(true);
    Axios.fetch(`page/${pageId}/slider`, {
      ...(sliderId ? {sliderId} : {}),
    })
      .then(res => {
        const {Page, Slider} = res.data?.data;
        
        // UPDATED: Get complete page data including pageType and dynamicEntities
        setPage({
          name: Page?.name || '',
          pageType: Page?.pageType || 'STATIC',
          dynamicEntities: Page?.dynamicEntities || [],
          route: Page?.route || ''
        });
        
        //updating fields
        reset({
          name: Slider?.name,
          status: Slider?.status || false,
          settings: JSON.parse(Slider?.settings) || '',
          banners: Slider?.Banners?.map(Banner => ({
            id: Banner?.id,
            title: Banner?.title,
            subTitle: Banner?.subTitle,
            link: Banner?.link,
            buttonText: Banner?.buttonText,
            position: Banner?.position,
            image: Banner?.image_url,
          })),
        });
        //hiding loader
        setIsLoading(false);
      })
      .catch(err => {
        console.log(err);
        //hiding loader
        setIsLoading(false);
      });
  };

  const handleFormSubmit = data => {
    //showing loader
    Block.circle('.form', `${sliderId ? 'Updating' : 'Adding'} Slider...`);
    //preparing form data
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('status', Number(data.status));
    formData.append('settings', data.settings);
    formData.append('deletedBanners', JSON.stringify(deletedBanners));
    //appending banners
    data.banners.forEach((banner, index) => {
      formData.append(`banners[${index}].title`, banner.title);
      formData.append(`banners[${index}].subTitle`, banner.subTitle);
      formData.append(`banners[${index}].link`, banner.link);
      formData.append(`banners[${index}].buttonText`, banner.buttonText);
      formData.append(`banners[${index}].position`, banner.position);
      if (banner.image && typeof banner.image !== 'string') {
        formData.append(`banners[${index}].image`, banner.image);
      }
      //appending id if exists
      if (banner.id) formData.append(`banners[${index}].id`, banner.id);
    });
    //appending total banners count
    formData.append('totalBanners', data.banners.length);
    //submitting form
    const Request = sliderId
      ? Axios.put(`page/${pageId}/slider/${sliderId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
      : Axios.post(`page/${pageId}/slider`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

    Request.then(res => {
      //hiding loader
      Block.remove('.form');
      //showing success message
      setShowSuccessSB(true);
      //redirecting to edit page
      if (!sliderId)
        setTimeout(() => {
          navigate(-1);
        }, 2000);
    }).catch(err => {
      console.log(err);
      //hiding loader
      Block.remove('.form');
      alert(err.response?.data?.message || `Error ${sliderId ? 'updating' : 'adding'} slider`);
    });
  };

  const handleAppendBanner = () => {
    //getting last banner position
    const lastBannerPosition =
      bannerFields.length > 0
        ? bannerFields[bannerFields.length - 1].position
        : 0;
    append({
      title: '',
      subTitle: '',
      position: lastBannerPosition + 1,
      image: null,
      link: '',
      buttonText: '',
    });
  };

  const banners = watch('banners');

  return (
    <div className="container">
      <DashboardLayout>
        <DashboardNavbar
          routes={[
            {name: 'Pages', link: 'pages'},
            {name: page?.name, link: 'pages/edit/' + pageId},
            {name: `${sliderId ? 'Update' : 'Add'} Slider`},
          ]}
        />

        {/* NEW: Page Information Section */}
        <Box mb={4} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Page Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                <strong>Page Name:</strong> {page.name}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                <strong>Route:</strong> {page.route}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                <strong>Page Type:</strong> 
                <Chip 
                  label={page.pageType} 
                  color={page.pageType === 'STATIC' ? 'info' : 'warning'} 
                  size="small" 
                  sx={{ ml: 1 }}
                />
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                <strong>Dynamic Entities:</strong>
              </Typography>
              {page.dynamicEntities && page.dynamicEntities.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {page.dynamicEntities.map((entity, index) => (
                    <Chip 
                      key={index}
                      label={entity.name}
                      color="primary"
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                  {page.pageType === 'DYNAMIC' ? 'Not Configured' : 'Not Applicable'}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>

        <form className="form" onSubmit={handleSubmit(handleFormSubmit)}>
          <Box mb={4}>
            <Typography variant="h5" gutterBottom>
              {sliderId ? 'Update' : 'Add'} Slider
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Controller
                  name="name"
                  control={control}
                  rules={{required: 'Name is required'}}
                  render={({field}) => (
                    <TextField
                      {...field}
                      label="Slider Name"
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      error={!!errors.name}
                      helperText={errors.name ? errors.name.message : ''}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="status"
                  control={control}
                  render={({field}) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          checked={field.value}
                          color="primary"
                        />
                      }
                      label="Active Status"
                      sx={{ mt: 2 }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="settings"
                  control={control}
                  rules={{required: false}}
                  render={({field}) => (
                    <TextField
                      {...field}
                      label="Slider Settings (JSON)"
                      variant="outlined"
                      fullWidth
                      multiline
                      margin="normal"
                      rows={4}
                      placeholder='{"autoplay": true, "speed": 500}'
                      error={!!errors.settings}
                      helperText={
                        errors.settings ? errors.settings.message : 'Enter settings in JSON format'
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
          <Box mb={4}>
            <Typography variant="h5" gutterBottom>
              Banners
            </Typography>
            <Grid container spacing={2}>
              {bannerFields.map((banner, index) => (
                <Grid
                  item
                  xs={12}
                  key={banner.id}
                  style={{
                    padding: '1rem',
                    border: '1px solid #e0e0e0',
                    borderRadius: '10px',
                    marginTop: '1rem',
                  }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={4}>
                      <Controller
                        name={`banners[${index}].title`}
                        control={control}
                        rules={{required: 'Title is required'}}
                        render={({field}) => (
                          <TextField
                            {...field}
                            label="Title"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            error={!!errors?.banners?.[index]?.title}
                            helperText={
                              errors?.banners?.[index]?.title
                                ? errors.banners[index].title.message
                                : ''
                            }
                          />
                        )}
                      />
                      <Controller
                        name={`banners[${index}].subTitle`}
                        control={control}
                        render={({field}) => (
                          <TextField
                            {...field}
                            label="Sub Title"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Controller
                        name={`banners[${index}].link`}
                        control={control}
                        rules={{required: 'Link is required'}}
                        render={({field}) => (
                          <TextField
                            {...field}
                            label="Link"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            error={!!errors?.banners?.[index]?.link}
                            helperText={
                              errors?.banners?.[index]?.link
                                ? errors.banners[index].link.message
                                : ''
                            }
                          />
                        )}
                      />
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6}>
                          <Controller
                            name={`banners[${index}].position`}
                            control={control}
                            rules={{required: 'Position is required', min: 1}}
                            render={({field}) => (
                              <TextField
                                {...field}
                                label="Position"
                                variant="outlined"
                                fullWidth
                                disabled
                                margin="normal"
                                type="number"
                                error={!!errors?.banners?.[index]?.position}
                                helperText={
                                  errors?.banners?.[index]?.position
                                    ? errors.banners[index].position.message
                                    : ''
                                }
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Controller
                            name={`banners[${index}].buttonText`}
                            control={control}
                            render={({field}) => (
                              <TextField
                                {...field}
                                label="Button Text"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={3}>
                      <Controller
                        name={`banners[${index}].image`}
                        control={control}
                        rules={{required: 'Image is required'}}
                        render={({field}) => (
                          <div>
                            <input
                              type="file"
                              id={`imageInput-${index}`}
                              hidden
                              onChange={e => {
                                if (e.target.files[0]) {
                                  update(index, {
                                    ...banners[index],
                                    image: e.target.files[0],
                                  });
                                }
                              }}
                            />
                            {banners[index]?.image ? (
                              <img
                                onClick={() =>
                                  document
                                    .getElementById(`imageInput-${index}`)
                                    .click()
                                }
                                src={
                                  typeof banners[index].image === 'string'
                                    ? banners[index].image
                                    : URL.createObjectURL(banners[index].image)
                                }
                                alt="banner"
                                style={{
                                  width: '100%',
                                  height: 'auto',
                                  cursor: 'pointer',
                                  maxHeight: '7rem',
                                  objectFit: 'contain',
                                }}
                              />
                            ) : (
                              <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                onClick={() =>
                                  document
                                    .getElementById(`imageInput-${index}`)
                                    .click()
                                }
                                style={{marginTop: '16px', color: 'blue'}}>
                                Upload Image
                              </Button>
                            )}
                            {errors?.banners?.[index]?.image && (
                              <Typography color="error" variant="body2">
                                {errors.banners[index].image.message}
                              </Typography>
                            )}
                          </div>
                        )}
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <IconButton
                        color="secondary"
                        onClick={() => {
                          remove(index);
                          if (banners[index]?.id)
                            //adding to deleted array
                            setDeletedBanners(old => [
                              ...old,
                              banners[index]?.id,
                            ]);
                        }}
                        style={{marginTop: '16px'}}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
            <MDButton
              variant="text"
              size="small"
              color="info"
              onClick={handleAppendBanner}
              style={{marginTop: '16px'}}>
              Add Banner +
            </MDButton>
            <br />
            <MDButton
              variant="gradient"
              color="info"
              type="submit"
              style={{marginTop: '16px'}}>
              {sliderId ? 'Update' : 'Create'} Slider
            </MDButton>
          </Box>
        </form>

        <MDSnackbar
          color="info"
          icon="notifications"
          title="Success"
          content={
            sliderId
              ? 'Slider updated Successfully'
              : 'Slider added Successfully'
          }
          open={showSuccessSB}
          onClose={closeSuccessSB}
          close={closeSuccessSB}
        />
        <Footer />
      </DashboardLayout>
    </div>
  );
}

export default Dashboard;

// import React, {useEffect, useState} from 'react';
// import {useForm, Controller, useFieldArray} from 'react-hook-form';
// import {
//   TextField,
//   Typography,
//   Box,
//   Grid,
//   Switch,
//   FormControlLabel,
//   Button,
//   IconButton,
// } from '@mui/material';
// import DeleteIcon from '@mui/icons-material/Delete';

// import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
// import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
// import Footer from 'examples/Footer';
// import MDButton from 'components/MDButton';
// import {useParams, useNavigate} from 'react-router-dom';
// import {Axios} from '../../services/axios';
// import {Block} from 'notiflix';
// import MDSnackbar from 'components/MDSnackbar';

// function Dashboard() {
//   const {
//     control,
//     handleSubmit,
//     watch,
//     reset,
//     formState: {errors},
//   } = useForm({
//     defaultValues: {
//       name: '',
//       status: false,
//       settings: '',
//       banners: [],
//     },
//   });

//   const {
//     fields: bannerFields,
//     append,
//     remove,
//     update,
//   } = useFieldArray({
//     control,
//     name: 'banners',
//   });

//   const navigate = useNavigate();
//   const {pageId, sliderId} = useParams();

//   //states
//   const [isLoading, setIsLoading] = useState(true);
//   const [showSuccessSB, setShowSuccessSB] = useState(false);
//   const [deletedBanners, setDeletedBanners] = useState([]);
//   const [page, setPage] = useState({name: ''});
//   //? Hooks
//   useEffect(() => {
//     fetchSliderData();
//   }, []);

//   useEffect(() => {
//     if (isLoading) Block.circle('.container', 'Loading Page Settings...');
//     else Block.remove('.container');
//   }, [isLoading]);

//   useEffect(() => {
//     if (showSuccessSB) {
//       setTimeout(() => {
//         closeSuccessSB();
//       }, 5000);
//     }
//   }, [showSuccessSB]);

//   //? Functions
//   const closeSuccessSB = () => setShowSuccessSB(false);

//   const fetchSliderData = async () => {
//     setIsLoading(true);
//     Axios.fetch(`page/${pageId}/slider`, {
//       ...(sliderId ? {sliderId} : {}),
//     })
//       .then(res => {
//         const {Page, Slider} = res.data?.data;
//         setPage(Page);
//         //updating fields
//         reset({
//           name: Slider?.name,
//           status: Slider?.status || false,
//           settings: JSON.parse(Slider?.settings) || '',
//           banners: Slider?.Banners?.map(Banner => ({
//             id: Banner?.id,
//             title: Banner?.title,
//             subTitle: Banner?.subTitle,
//             link: Banner?.link,
//             buttonText: Banner?.buttonText,
//             position: Banner?.position,
//             image: Banner?.image_url,
//           })),
//         });
//         //hiding loader
//         setIsLoading(false);
//       })
//       .catch(err => {
//         console.log(err);
//         //hiding loader
//         setIsLoading(false);
//       });
//   };

//   const handleFormSubmit = data => {
//     //showing loader
//     Block.circle('.form', 'Adding Slider...');
//     //preparing form data
//     const formData = new FormData();
//     formData.append('name', data.name);
//     formData.append('status', Number(data.status));
//     formData.append('settings', data.settings);
//     formData.append('deletedBanners', JSON.stringify(deletedBanners));
//     //appending banners
//     data.banners.forEach((banner, index) => {
//       formData.append(`banners[${index}].title`, banner.title);
//       formData.append(`banners[${index}].subTitle`, banner.subTitle);
//       formData.append(`banners[${index}].link`, banner.link);
//       formData.append(`banners[${index}].buttonText`, banner.buttonText);
//       formData.append(`banners[${index}].position`, banner.position);
//       formData.append(`banners[${index}].image`, banner.image);
//       //appending id if exists
//       if (banner.id) formData.append(`banners[${index}].id`, banner.id);
//     });
//     //appending total banners count
//     formData.append('totalBanners', data.banners.length);
//     //submitting form
//     const Request = sliderId
//       ? Axios.put(`page/${pageId}/slider/${sliderId}`, formData)
//       : Axios.post(`page/${pageId}/slider`, formData);

//     Request.then(res => {
//       //hiding loader
//       Block.remove('.form');
//       //showing success message
//       setShowSuccessSB(true);
//       //redirecting to edit page
//       if (!sliderId)
//         setTimeout(() => {
//           navigate(-1);
//         }, 2000);
//     }).catch(err => {
//       console.log(err);
//       //hiding loader
//       Block.remove('.form');
//     });
//   };

//   const handleAppendBanner = () => {
//     //getting last banner position
//     const lastBannerPosition =
//       bannerFields.length > 0
//         ? bannerFields[bannerFields.length - 1].position
//         : 0;
//     append({
//       title: '',
//       subTitle: '',
//       position: lastBannerPosition + 1,
//       image: null,
//       link: '',
//       buttonText: '',
//     });
//   };

//   const banners = watch('banners');

//   return (
//     <div className="container">
//       <DashboardLayout>
//         <DashboardNavbar
//           routes={[
//             {name: 'Pages', link: 'pages'},
//             {name: page?.name, link: 'pages/edit/' + pageId},
//             {name: `${sliderId ? 'Update' : 'Add'} Slider`},
//           ]}
//         />

//         <form className="form" onSubmit={handleSubmit(handleFormSubmit)}>
//           <Box mb={4}>
//             <Typography variant="h5" gutterBottom>
//               {sliderId ? 'Update' : 'Add'} Slider
//             </Typography>
//             <Grid container spacing={2}>
//               <Grid item xs={6}>
//                 <Controller
//                   name="name"
//                   control={control}
//                   rules={{required: 'Name is required'}}
//                   render={({field}) => (
//                     <TextField
//                       {...field}
//                       label="Name"
//                       variant="outlined"
//                       fullWidth
//                       margin="normal"
//                       error={!!errors.name}
//                       helperText={errors.name ? errors.name.message : ''}
//                     />
//                   )}
//                 />
//               </Grid>
//               <Grid item xs={6}>
//                 <Controller
//                   name="status"
//                   control={control}
//                   render={({field}) => (
//                     <FormControlLabel
//                       control={
//                         <Switch
//                           {...field}
//                           checked={field.value}
//                           color="primary"
//                         />
//                       }
//                       label="Active Status"
//                     />
//                   )}
//                 />
//               </Grid>
//               <Grid item xs={6}>
//                 <Controller
//                   name="settings"
//                   control={control}
//                   rules={{required: false}}
//                   render={({field}) => (
//                     <TextField
//                       {...field}
//                       label="Settings"
//                       variant="outlined"
//                       fullWidth
//                       multiline
//                       margin="normal"
//                       rows={4}
//                       error={!!errors.settings}
//                       helperText={
//                         errors.settings ? errors.settings.message : ''
//                       }
//                     />
//                   )}
//                 />
//               </Grid>
//             </Grid>
//           </Box>
//           <Box mb={4}>
//             <Typography variant="h5" gutterBottom>
//               Banners
//             </Typography>
//             <Grid container spacing={2}>
//               {bannerFields.map((banner, index) => (
//                 <Grid
//                   item
//                   xs={12}
//                   key={banner.id}
//                   style={{
//                     padding: '1rem',
//                     border: '1px solid #e0e0e0',
//                     borderRadius: '10px',
//                     marginTop: '1rem',
//                   }}>
//                   <Grid container spacing={2} alignItems="center">
//                     <Grid item xs={4}>
//                       <Controller
//                         name={`banners[${index}].title`}
//                         control={control}
//                         rules={{required: 'Title is required'}}
//                         render={({field}) => (
//                           <TextField
//                             {...field}
//                             label="Title"
//                             variant="outlined"
//                             fullWidth
//                             margin="normal"
//                             error={!!errors?.banners?.[index]?.title}
//                             helperText={
//                               errors?.banners?.[index]?.title
//                                 ? errors.banners[index].title.message
//                                 : ''
//                             }
//                           />
//                         )}
//                       />
//                       <Controller
//                         name={`banners[${index}].subTitle`}
//                         control={control}
//                         render={({field}) => (
//                           <TextField
//                             {...field}
//                             label="Sub Title"
//                             variant="outlined"
//                             fullWidth
//                             margin="normal"
//                           />
//                         )}
//                       />
//                     </Grid>
//                     <Grid item xs={4}>
//                       <Controller
//                         name={`banners[${index}].link`}
//                         control={control}
//                         rules={{required: 'Link is required'}}
//                         render={({field}) => (
//                           <TextField
//                             {...field}
//                             label="Link"
//                             variant="outlined"
//                             fullWidth
//                             margin="normal"
//                             error={!!errors?.banners?.[index]?.link}
//                             helperText={
//                               errors?.banners?.[index]?.link
//                                 ? errors.banners[index].link.message
//                                 : ''
//                             }
//                           />
//                         )}
//                       />
//                       <Grid container spacing={2} alignItems="center">
//                         <Grid item xs={6}>
//                           <Controller
//                             name={`banners[${index}].position`}
//                             control={control}
//                             rules={{required: 'Position is required', min: 1}}
//                             render={({field}) => (
//                               <TextField
//                                 {...field}
//                                 label="Position"
//                                 variant="outlined"
//                                 fullWidth
//                                 disabled
//                                 margin="normal"
//                                 type="number"
//                                 error={!!errors?.banners?.[index]?.position}
//                                 helperText={
//                                   errors?.banners?.[index]?.position
//                                     ? errors.banners[index].position.message
//                                     : ''
//                                 }
//                               />
//                             )}
//                           />
//                         </Grid>
//                         <Grid item xs={6}>
//                           <Controller
//                             name={`banners[${index}].buttonText`}
//                             control={control}
//                             render={({field}) => (
//                               <TextField
//                                 {...field}
//                                 label="Button Text"
//                                 variant="outlined"
//                                 fullWidth
//                                 margin="normal"
//                               />
//                             )}
//                           />
//                         </Grid>
//                       </Grid>
//                     </Grid>
//                     <Grid item xs={3}>
//                       <Controller
//                         name={`banners[${index}].image`}
//                         control={control}
//                         rules={{required: 'Image is required'}}
//                         render={({field}) => (
//                           <div>
//                             <input
//                               type="file"
//                               id={`imageInput-${index}`}
//                               hidden
//                               onChange={e => {
//                                 if (e.target.files[0]) {
//                                   update(index, {
//                                     ...banners[index],
//                                     image: e.target.files[0],
//                                   });
//                                 }
//                               }}
//                             />
//                             {banners[index]?.image ? (
//                               <img
//                                 onClick={() =>
//                                   document
//                                     .getElementById(`imageInput-${index}`)
//                                     .click()
//                                 }
//                                 src={
//                                   typeof banners[index].image === 'string'
//                                     ? banners[index].image
//                                     : URL.createObjectURL(banners[index].image)
//                                 }
//                                 alt="banner"
//                                 style={{
//                                   width: '100%',
//                                   height: 'auto',
//                                   cursor: 'pointer',
//                                   maxHeight: '7rem',
//                                   objectFit: 'contain',
//                                 }}
//                               />
//                             ) : (
//                               <Button
//                                 variant="outlined"
//                                 component="label"
//                                 fullWidth
//                                 onClick={() =>
//                                   document
//                                     .getElementById(`imageInput-${index}`)
//                                     .click()
//                                 }
//                                 style={{marginTop: '16px', color: 'blue'}}>
//                                 Upload Image
//                               </Button>
//                             )}
//                             {errors?.banners?.[index]?.image && (
//                               <Typography color="error" variant="body2">
//                                 {errors.banners[index].image.message}
//                               </Typography>
//                             )}
//                           </div>
//                         )}
//                       />
//                     </Grid>
//                     <Grid item xs={1}>
//                       <IconButton
//                         color="secondary"
//                         onClick={() => {
//                           remove(index);
//                           if (banners[index]?.id)
//                             //adding to deleted array
//                             setDeletedBanners(old => [
//                               ...old,
//                               banners[index]?.id,
//                             ]);
//                         }}
//                         style={{marginTop: '16px'}}>
//                         <DeleteIcon />
//                       </IconButton>
//                     </Grid>
//                   </Grid>
//                 </Grid>
//               ))}
//             </Grid>
//             <MDButton
//               variant="text"
//               size="small"
//               color="info"
//               onClick={handleAppendBanner}
//               style={{marginTop: '16px'}}>
//               Add Banner +
//             </MDButton>
//             <br />
//             <MDButton
//               variant="gradient"
//               color="info"
//               type="submit"
//               style={{marginTop: '16px'}}>
//               Submit
//             </MDButton>
//           </Box>
//         </form>

//         <MDSnackbar
//           color="info"
//           icon="notifications"
//           title="Success"
//           content={
//             sliderId
//               ? 'Slider updated Successfully'
//               : 'Slider added Successfully'
//           }
//           open={showSuccessSB}
//           onClose={closeSuccessSB}
//           close={closeSuccessSB}
//         />
//         <Footer />
//       </DashboardLayout>
//     </div>
//   );
// }

// export default Dashboard;
